// Address form component
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { addressSchema, type AddressInput } from '../../../shared/types'
import { useTranslation } from '../../hooks/useTranslation'
import { AddressAutocomplete, type ParsedAddress } from '../ui/AddressAutocomplete'

interface Props {
  defaultValues?: Partial<AddressInput>
  onSubmit: (data: AddressInput) => void
  onCancel?: () => void
  loading?: boolean
  accountId: string
  disabledTypes?: ('primary' | 'billing' | 'shipping')[]
}

export function AddressForm({ defaultValues, onSubmit, onCancel, loading, accountId, disabledTypes = [] }: Props) {
  const { t } = useTranslation()
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<AddressInput>({
    resolver: zodResolver(addressSchema),
    defaultValues: { ...defaultValues, accountId },
  })

  const handleAddressSelect = (address: ParsedAddress) => {
    setValue('street', address.street, { shouldValidate: true })
    setValue('city', address.city, { shouldValidate: true })
    setValue('state', address.state, { shouldValidate: true })
    setValue('postalCode', address.postalCode, { shouldValidate: true })
    setValue('country', address.country, { shouldValidate: true })
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      {/* Address Search */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('fields.searchAddress')}
        </label>
        <AddressAutocomplete
          onSelect={handleAddressSelect}
          placeholder={t('fields.searchAddressPlaceholder')}
        />
        <p className="mt-1 text-xs text-gray-500">
          {t('fields.searchAddressHelp')}
        </p>
      </div>

      <div className="border-t border-gray-200 pt-4">
        <label htmlFor="type" className="block text-sm font-medium text-gray-700">
          {t('fields.addressType')} <span className="text-red-500">*</span>
        </label>
        <select
          {...register('type')}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
        >
          <option value="">{t('common.select')}</option>
          <option value="primary" disabled={disabledTypes.includes('primary')}>
            {t('status.primary')}
          </option>
          <option value="billing" disabled={disabledTypes.includes('billing')}>
            {t('status.billing')}
          </option>
          <option value="shipping">{t('status.shipping')}</option>
        </select>
        {errors.type && (
          <p className="mt-1 text-sm text-red-600">{errors.type.message}</p>
        )}
      </div>

      <div>
        <label htmlFor="street" className="block text-sm font-medium text-gray-700">
          {t('fields.street')} <span className="text-red-500">*</span>
        </label>
        <input
          {...register('street')}
          type="text"
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
        />
        {errors.street && (
          <p className="mt-1 text-sm text-red-600">{errors.street.message}</p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="city" className="block text-sm font-medium text-gray-700">
            {t('fields.city')} <span className="text-red-500">*</span>
          </label>
          <input
            {...register('city')}
            type="text"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
          />
          {errors.city && (
            <p className="mt-1 text-sm text-red-600">{errors.city.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="state" className="block text-sm font-medium text-gray-700">
            {t('fields.state')}
          </label>
          <input
            {...register('state')}
            type="text"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
          />
          {errors.state && (
            <p className="mt-1 text-sm text-red-600">{errors.state.message}</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="postalCode" className="block text-sm font-medium text-gray-700">
            {t('fields.postalCode')}
          </label>
          <input
            {...register('postalCode')}
            type="text"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
          />
          {errors.postalCode && (
            <p className="mt-1 text-sm text-red-600">{errors.postalCode.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="country" className="block text-sm font-medium text-gray-700">
            {t('fields.country')} <span className="text-red-500">*</span>
          </label>
          <input
            {...register('country')}
            type="text"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
          />
          {errors.country && (
            <p className="mt-1 text-sm text-red-600">{errors.country.message}</p>
          )}
        </div>
      </div>

      <input type="hidden" {...register('accountId')} value={accountId} />

      <div className="flex justify-end gap-3 pt-4">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            {t('common.cancel')}
          </button>
        )}
        <button
          type="submit"
          disabled={loading}
          className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
        >
          {loading ? t('common.saving') : t('common.save')}
        </button>
      </div>
    </form>
  )
}
